import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogContent,
  Switch,
  TableSortLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import Testimonials from "./Testimonials";

// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.id,
  },
  {
    key: "stateid",
    label: "State ID",
    cellCss: { align: "center", minWidth: "70px" },
    getValue: (r) => r.stateId,
  },
  {
    key: "universityid",
    label: "University ID",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.universityId,
  },
  {
    key: "courseid",
    label: "Course ID",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.courseId,
  },
  {
    key: "imageUrl",
    label: "Image Url",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.imageUrl,
  },
  {
    key: "firstName",
    label: "First Name",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.firstName,
  },
  {
    key: "lastName",
    label: "Last Name",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.lastName,
  },
  {
    key: "stateName",
    label: "State Name",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.stateName,
  },
  {
    key: "universityName",
    label: "University Name",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.universityName,
  },
  {
    key: "courseName",
    label: "Course Name",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.courseName,
  },
  {
    key: "content",
    label: "Content",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.content,
  },
  {
    key: "professions",
    label: "Professions",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.professions,
  },
  {
    key: "displayOrder",
    label: "Display Order",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.displayOrder,
  },
  {
    key: "active",
    label: "Active",
    getValue: (r) => (r.active ? "Yes" : "No"),
  },
];
const defaultVisibleKeys = new Set([
  "imageUrl",
  "firstName",
  "lastName",
  // "stateName",
  // "universityName",
  // "courseName",
  "content",
  "professions",
  "active",
]);

const STORAGE_KEY = "testimonialsVisibleColumns";

function TestimonialTable() {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [selectedstates, setSelectedStates] = useState("1" || "");
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("testimonials_management") || "";
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [testmonialsEdit, setTestmonialsEdit] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  const [filterState, setFilterState] = useState("");
  const [filterUniversity, setFilterUniversity] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const [states, setStates] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);

  // 1️Filter
  const filteredTestimonials = testimonials.filter((t) => {
    if (
      searchQuery &&
      !t.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !t.lastName?.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    if (filterState && t.stateId !== filterState) return false;
    if (filterUniversity && t.universityId !== filterUniversity) return false;
    if (filterCourse && t.courseId !== filterCourse) return false;
    return true;
  });
  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("testimonials_management", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  // Inside your component
  const sortedData = useMemo(() => {
    if (!filteredTestimonials) return [];

    return [...filteredTestimonials].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredTestimonials, orderBy, order]);
  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };
  const handleChange = (event) => {
    setSelectedStates(event.target.value);
  };
  console.log(testimonials);

  //Fetching the testimonials data with states id
  useEffect(() => {
    setIsLoading(true);
    api
      .get(`/api/v1/testimonials/active`)
      .then((res) => setTestimonials(res.data.data))
      .catch((error) => {
        console.error("There was an error fetching the book details!", error);
        setError("Failed to load books. Please try again.");
      })
      .finally(() => setIsLoading(false));
    api.get("/getAllStates").then((res) => setStates(res.data.data ?? []));
    api
      .get("/getAllUniversities")
      .then((res) => setUniversities(res.data.data ?? []));
    api.get("/getCourses", { params: { active: true } }).then((res) => setCourses(res.data.data ?? []));
  }, []);
  //Fetching the States get Data

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const validKeys = new Set(allColumns.map((c) => c.key));
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(
      Array.from(defaultVisibleKeys).filter((k) => validKeys.has(k)),
    );
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    const normalized = value === true || value === "true" || value === "Yes";
    return (
      <Chip
        label={normalized ? "Yes" : "No"}
        color={normalized ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  // pagenation code
  const paginatedTestimonials = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The banner record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/testimonials/${selectedId}`);

      // If deletion is successful, update local state update
      setTestimonials((prev) => prev.filter((row) => row.id !== selectedId));
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setTestmonialsEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setTestmonialsEdit(rowData);
  };
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // update locally
    setTestimonials((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      // If editing: replace the item
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        // If adding: push new item
        return [newItem, ...prev];
      }
    });

    setTestmonialsEdit(null);
  };
  return (
    <>
      {/* <NavBar /> */}
      <ToastContainer />
      {/* <Card sx={{ mb: 3, boxShadow: 2, width: "100%", mt: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" to="/booklist">
                  Books List
                </Link>
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  Testimonial Management
                </Typography>
              </Breadcrumbs>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Testimonials
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Refresh"}
            </Button>
          </Box>

         
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              {...BUTTON_STYLES.primary}
            >
              Add Testimonials
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{ minWidth: 120 }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
          </Stack>
        </CardContent>
      </Card> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
          marginLeft: "10px",
          marginTop: "10px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            Testimonial Management
          </Typography>
        </Breadcrumbs>

        <Box>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
              mt: { xs: 1, md: 0 },
              marginRight: "10px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
              PaperProps={{
                sx: {
                  width: 200,
                  maxHeight: 500,
                  overflowY: "auto",
                  borderRadius: 2,
                },
              }}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              {...BUTTON_STYLES.primary}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Add Testimonials
            </Button>
          </Stack>
        </Box>
      </Box>
      {/* Search Section */}
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                gap: 1,
                flex: 1,
              }}
            >
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Search name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={filterState}
                  onChange={(e) => {
                    setFilterState(e.target.value);
                    setFilterUniversity("");
                    setFilterCourse("");
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>University</InputLabel>
                <Select
                  label="University"
                  value={filterUniversity}
                  onChange={(e) => {
                    setFilterUniversity(e.target.value);
                    setFilterCourse("");
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {(filterState
                    ? universities.filter((u) => u.stateId === filterState)
                    : universities
                  ).map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Course</InputLabel>
                <Select
                  label="Course"
                  value={filterCourse}
                  onChange={(e) => setFilterCourse(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {(filterUniversity
                    ? courses.filter((c) => c.universityId === filterUniversity)
                    : courses
                  ).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.courseName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Tooltip title="Dense padding" arrow>
              <FormControlLabel
                control={
                  <Switch
                    checked={dense}
                    onChange={handleChangeDense}
                    color="primary"
                  />
                }
              />
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: "start" }}
          >
            {sortedData.length} of {testimonials.length} Testimonials items
          </Typography>
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedTestimonials) &&
                  paginatedTestimonials.length > 0 ? (
                    paginatedTestimonials
                      .filter(
                        (testimonial) =>
                          !searchQuery ||
                          testimonial.firstName
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          testimonial.lastName
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {visibleColumnDefs.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{ padding: "10px 10px", ...c.cellCss }}
                            >
                              {(() => {
                                const value = c.getValue(row);
                                const key = c.key;

                                // Optional debug

                                if (key === "active" || key === "isNational") {
                                  return getBooleanChip(value);
                                }

                                if (key === "contentType") {
                                  return getContentTypeChip(value);
                                }

                                if (key === "imageUrl") {
                                  return key === "imageUrl" ? (
                                    <img
                                      src={`https://siaedutech.com/public/${value}`}
                                      alt="preview"
                                      style={{
                                        width: "30px",
                                        height: "auto",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                      onClick={(e) => {
                                        e.currentTarget.blur();
                                        setOpenImage(
                                          `https://siaedutech.com/public/${value}`,
                                        );
                                      }}
                                    />
                                  ) : (
                                    value // show fallback text if not valid image
                                  );
                                }

                                return value;
                              })()}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              align: "center",
                              padding: "10px 10px",
                              minWidth: "120px",
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <Tooltip
                                title="Edit Testimonial"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title="Delete Testimonial"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  color="error"
                                  onClick={() => handleClickOpen(row.id)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No news found matching your search"
                              : "No news available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={filteredTestimonials.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      {/* Banner modal */}
      <Testimonials
        open={openNew}
        onClose={handleNewClose}
        testmonialsEdit={testmonialsEdit}
        onSuccess={handleFormSuccess}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
        disableAutoFocus={false}
        disableEnforceFocus={false}
        disableRestoreFocus={false}
      >
        <DialogContent>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TestimonialTable;
